/**
 * Sentence-level highlight map for TTS narration.
 *
 * Walks each paragraph's text nodes via TreeWalker, splits on sentence
 * boundaries (.!? followed by whitespace + opening capital / quote /
 * apostrophe), builds a flat list of sentences with:
 *   1. A paragraph reference (for scroll fallback + paragraph-class swap)
 *   2. A precomputed Range object (for CSS Highlight API)
 *
 * No DOM rewriting — sentences are addressed by `(textNode, offset)` pairs
 * so the drop-cap, inline links and selection behavior all stay intact.
 *
 * CSS Highlight API: Chrome 105+, Firefox 117+, Safari 17.4+. Older
 * browsers automatically fall back to paragraph-level highlighting via
 * the `.xot-tts-active` class — no double-highlighting (we skip the
 * class when the API is available to avoid coral-sidebar + sentence-bg
 * on the same line).
 *
 * Exclusion contract: imports `DENY_SELECTORS` from `./text-extract.ts`
 * so the highlight map and the text extractor share a single authoritative
 * deny list. Phase 2 bite 4 — eliminates the parallel DENY copy that
 * previously lived inline in single-article.js and would drift over time.
 *
 * Build path:
 *   src   assets/js/src/single-article/highlight-map.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/highlight-map.js  (attaches window.XOT.article.highlight)
 *
 * Test path:
 *   src   assets/js/src/single-article/highlight-map.ts
 *   ↓ vitest (jsdom env)
 *   test  tests/js/single-article-highlight-map.test.ts
 *
 * @since Phase 2 — JS modernization, bite 4
 */

import { DENY_SELECTORS } from './text-extract';

const ALLOW_TAGS        = 'p, h2, h3, h4, h5, h6, li, blockquote';
const MIN_PARA_LEN      = 4;
const HIGHLIGHT_NAME    = 'xot-tts-sentence';
const ACTIVE_CLASS      = 'xot-tts-active';
const SCROLL_VERT_PAD   = 80;   // px reserved at top + bottom before triggering scrollIntoView

/* Sentence-splitting regex. Greedy on terminal punctuation, requires
   following whitespace then an opening uppercase / quote / apostrophe.
   Doesn't split on "Mr. Smith" (no whitespace before lowercase) or "3.14"
   (digit, not capital). Conservative — undersplits in edge cases (abbr.
   followed by Capital). That's fine: undersplit reads as a single
   slightly-longer highlighted segment, never wrong. */
const SENT_BREAK_RE = /([.!?]+)\s+(?=[A-Z"'“])/g;

export interface SentenceEntry {
    readonly paraNode:   Element;
    readonly paraIdx:    number;
    readonly charOffset: number;
    readonly charLen:    number;
    readonly range:      Range | null;
}

export interface ParaEntry {
    readonly node:       Element;
    readonly charOffset: number;
    readonly charLen:    number;
}

/* ── Module-private state ─────────────────────────────────────────────
   Memoized on first build. Reset via resetHighlightMap() for SPA nav
   or unit tests. */
let _sentenceMap:   SentenceEntry[] | null = null;
let _paraMap:       ParaEntry[]     | null = null;
let _totalChars                            = 0;
let _activeSentIdx                         = -1;
let _activeParaIdx                         = -1;

/**
 * Detect CSS Highlight API support at call time (not module load) so
 * tests can mock it. Production capability is static after first read.
 */
function isHighlightCapable(): boolean {
    return typeof CSS !== 'undefined'
        && !!(CSS as { highlights?: unknown }).highlights
        && typeof (globalThis as { Highlight?: unknown }).Highlight === 'function';
}

/**
 * Return true when element `n` is inside any deny-listed region, or has
 * aria-hidden="true" set on itself. Uses the shared {@link DENY_SELECTORS}
 * from `text-extract.ts` so a single source of truth governs both the
 * narration extractor and the highlight walker.
 */
export function isBlockExcluded(n: Element): boolean {
    for (const sel of DENY_SELECTORS) {
        if (n.closest && n.closest(sel)) return true;
    }
    if (n.getAttribute && n.getAttribute('aria-hidden') === 'true') return true;
    return false;
}

/**
 * Build per-sentence entries for a single paragraph node.
 * No DOM mutation — sentences are addressed by Range objects over the
 * paragraph's text nodes.
 */
export function buildParaSentences(
    paraNode: Element,
    paraIdx: number,
    globalCharOffset: number,
): SentenceEntry[] {
    /* Walk all text nodes inside the paragraph + assemble its plain text. */
    const walker = document.createTreeWalker(paraNode, NodeFilter.SHOW_TEXT, {
        acceptNode(n: Node): number {
            return n.nodeValue && n.nodeValue.trim().length
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT;
        },
    } as NodeFilter);
    const textNodes: Array<{ node: Text; start: number }> = [];
    let fullText = '';
    let tn: Node | null;
    while ((tn = walker.nextNode())) {
        textNodes.push({ node: tn as Text, start: fullText.length });
        fullText += tn.nodeValue;
    }
    if (textNodes.length === 0) return [];

    /* Locate (textNode, offset) for a global char index within fullText. */
    function locate(charIdx: number): { node: Text; offset: number } {
        for (const t of textNodes) {
            const endIn = t.start + t.node.nodeValue!.length;
            if (charIdx <= endIn) {
                return { node: t.node, offset: Math.max(0, charIdx - t.start) };
            }
        }
        const last = textNodes[textNodes.length - 1];
        return { node: last.node, offset: last.node.nodeValue!.length };
    }

    /* Split on sentence boundaries. */
    const boundaries: Array<{ end: number; nextStart: number }> = [];
    SENT_BREAK_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = SENT_BREAK_RE.exec(fullText)) !== null) {
        boundaries.push({ end: m.index + m[1].length, nextStart: SENT_BREAK_RE.lastIndex });
    }
    const sentences: Array<{ start: number; end: number }> = [];
    let cursor = 0;
    for (const b of boundaries) {
        sentences.push({ start: cursor, end: b.end });
        cursor = b.nextStart;
    }
    if (cursor < fullText.length) sentences.push({ start: cursor, end: fullText.length });
    if (sentences.length === 0)   sentences.push({ start: 0,      end: fullText.length });

    /* Convert each sentence to a SentenceEntry. Range built only when the
       CSS Highlight API is supported — otherwise the fallback paragraph
       class swap is what renders, so the Range would be unused weight. */
    const capable = isHighlightCapable();
    const out: SentenceEntry[] = [];
    let paraOff = 0;
    for (const s of sentences) {
        const charLen = Math.max(1, s.end - s.start);
        let range: Range | null = null;
        if (capable) {
            try {
                const startLoc = locate(s.start);
                const endLoc   = locate(s.end);
                const r = document.createRange();
                r.setStart(startLoc.node, startLoc.offset);
                r.setEnd(endLoc.node, endLoc.offset);
                range = r;
            } catch { range = null; }
        }
        out.push({
            paraNode,
            paraIdx,
            charOffset: globalCharOffset + paraOff,
            charLen,
            range,
        });
        paraOff += charLen + 1; // +1 for the implicit space between sentences
    }
    return out;
}

/**
 * Build the full document's sentence map. Memoized — subsequent calls
 * return the cached result. Call {@link resetHighlightMap} to invalidate
 * after dynamic content insertion (autolinker, SPA navigation).
 */
export function buildHighlightMap(): SentenceEntry[] {
    if (_sentenceMap) return _sentenceMap;
    const body = document.querySelector('.xot-article__body');
    if (!body) {
        _sentenceMap = [];
        _paraMap     = [];
        return _sentenceMap;
    }
    const paras = body.querySelectorAll(ALLOW_TAGS);
    const sentences:   SentenceEntry[] = [];
    const paraEntries: ParaEntry[]     = [];
    let offset = 0;
    paras.forEach(p => {
        if (isBlockExcluded(p)) return;
        const pText = ((p as HTMLElement).innerText ?? p.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (pText.length < MIN_PARA_LEN) return;
        const paraIdx = paraEntries.length;
        const subs    = buildParaSentences(p, paraIdx, offset);
        if (subs.length === 0) return;
        paraEntries.push({ node: p, charOffset: offset, charLen: pText.length });
        for (const s of subs) sentences.push(s);
        offset += pText.length + 2; // +2 for the inter-paragraph break
    });
    _sentenceMap = sentences;
    _paraMap     = paraEntries;
    _totalChars  = offset;
    return _sentenceMap;
}

/**
 * Activate the sentence at `idx`. Applies the CSS Highlight when the
 * Highlight API is available; otherwise toggles the `.xot-tts-active`
 * class on the host paragraph. Idempotent for the same idx.
 *
 * Paragraph-change side effect: smoothly scrolls the new paragraph into
 * view when it isn't already in the safe band (top/bottom padding).
 * Sentence-granular scrolling would jitter, so we only scroll on
 * paragraph transitions.
 */
export function setActiveSentence(idx: number): void {
    if (idx === _activeSentIdx) return;
    if (!_sentenceMap || _sentenceMap.length === 0) return;
    const entry = _sentenceMap[idx];
    if (!entry) return;
    _activeSentIdx = idx;

    const capable = isHighlightCapable();
    if (capable && entry.range) {
        try {
            const cssAny = CSS as { highlights: { set: (n: string, h: object) => void } };
            const HL     = (globalThis as { Highlight: new (r: Range) => object }).Highlight;
            cssAny.highlights.set(HIGHLIGHT_NAME, new HL(entry.range));
        } catch { /* runtime fallback */ }
    }

    if (entry.paraIdx !== _activeParaIdx) {
        if (_activeParaIdx >= 0 && _paraMap?.[_activeParaIdx]) {
            _paraMap[_activeParaIdx].node.classList.remove(ACTIVE_CLASS);
        }
        _activeParaIdx = entry.paraIdx;
        const para = _paraMap?.[_activeParaIdx];
        if (para) {
            if (!capable) para.node.classList.add(ACTIVE_CLASS);
            const r = para.node.getBoundingClientRect();
            const inView = r.top >= SCROLL_VERT_PAD
                        && r.bottom <= (window.innerHeight - SCROLL_VERT_PAD);
            if (!inView) {
                try { para.node.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
                catch { /* unsupported in some environments — silent */ }
            }
        }
    }
}

/**
 * Clear any active highlight. Removes the CSS Highlight registration and
 * every `.xot-tts-active` paragraph class.
 */
export function clearHighlight(): void {
    if (isHighlightCapable()) {
        try {
            const cssAny = CSS as { highlights: { delete: (n: string) => void } };
            cssAny.highlights.delete(HIGHLIGHT_NAME);
        } catch { /* silent */ }
    }
    if (_paraMap) {
        for (const e of _paraMap) e.node.classList.remove(ACTIVE_CLASS);
    }
    _activeSentIdx = -1;
    _activeParaIdx = -1;
}

/**
 * Map an audio currentTime → sentence index by proportional char offset.
 * Linear scan over 30-80 sentences per article is negligible at the
 * ~4 Hz `timeupdate` cadence.
 */
export function highlightFromTime(t: number, duration: number): void {
    const map = buildHighlightMap();
    if (map.length === 0 || _totalChars === 0 || !duration) return;
    const charsPerSec = _totalChars / duration;
    const currentChar = t * charsPerSec;
    let idx = -1;
    for (let i = 0; i < map.length; i++) {
        if (currentChar >= map[i].charOffset && currentChar < map[i].charOffset + map[i].charLen) {
            idx = i;
            break;
        }
        if (currentChar < map[i].charOffset) {
            idx = Math.max(0, i - 1);
            break;
        }
    }
    if (idx === -1) idx = map.length - 1;
    setActiveSentence(idx);
}

/**
 * Reset the memoized map + indices. Use after the article body has been
 * replaced (SPA nav) or in unit tests between cases.
 */
export function resetHighlightMap(): void {
    _sentenceMap   = null;
    _paraMap       = null;
    _totalChars    = 0;
    _activeSentIdx = -1;
    _activeParaIdx = -1;
}

/** Inspector helper for tests / debug overlays. Do not rely on shape. */
export function _debugSnapshot(): {
    sentenceCount: number;
    paraCount:     number;
    totalChars:    number;
    activeSent:    number;
    activePara:    number;
} {
    return {
        sentenceCount: _sentenceMap?.length ?? 0,
        paraCount:     _paraMap?.length     ?? 0,
        totalChars:    _totalChars,
        activeSent:    _activeSentIdx,
        activePara:    _activeParaIdx,
    };
}

/* ── window.XOT.article.highlight attachment (legacy IIFE consumers) ──
   single-article.js IIFE will consume this surface; falls back to a
   thin inline shim if the lib isn't loaded yet. */
interface XotArticleHighlight {
    build:            typeof buildHighlightMap;
    setActive:        typeof setActiveSentence;
    clear:            typeof clearHighlight;
    fromTime:         typeof highlightFromTime;
    reset:            typeof resetHighlightMap;
    isBlockExcluded:  typeof isBlockExcluded;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:      unknown;
                highlight?: XotArticleHighlight;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.highlight = {
        build:           buildHighlightMap,
        setActive:       setActiveSentence,
        clear:           clearHighlight,
        fromTime:        highlightFromTime,
        reset:           resetHighlightMap,
        isBlockExcluded,
    };
    xot.article = article;
    window.XOT  = xot;
}
